import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { startOfMonth, endOfMonth } from "date-fns";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { DateField, fmtISO } from "./_shared";
import DrillDownModal from "@/components/DrillDownModal";

export function ExpenseByVendorReport() {
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(endOfMonth(new Date()));
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const { isActive, filterRails } = useCurrencyRail();

  const load = useCallback(async () => {
    const { data } = await api.get("/reports/expense-by-vendor", {
      params: { start: fmtISO(start), end: fmtISO(end) },
    });
    setData(data);
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const rails = data ? filterRails(data.by_currency || []) : [];
  const visible = data ? data.rows.filter((r) => isActive(r.currency || "USD")) : [];

  return (
    <div data-testid="ebv-report" className="space-y-6">
      <div className="flex items-end gap-3 flex-wrap">
        <DateField label="From" value={start} onChange={setStart} testId="ebv-start" />
        <DateField label="To" value={end} onChange={setEnd} testId="ebv-end" />
      </div>
      {data && (
        <>
          {rails.map((r) => (
            <div key={`ebv-rail-${r.currency}`} data-testid={`ebv-rail-${r.currency}`} className="grid grid-cols-5 gap-4 items-stretch">
              <div className="card-flat p-4 border-l-4" style={{ borderLeftColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}>
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{r.currency} rail</div>
                <div className="font-mono-data text-lg mt-1">
                  <span className="px-1.5 py-0.5 rounded text-xs text-[#FDFCF8]"
                        style={{ backgroundColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}>{r.currency}</span>
                </div>
              </div>
              <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Bills</div><div className="font-mono-data text-lg mt-1">{r.bills}</div></div>
              <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Billed</div><div className="font-mono-data text-lg mt-1">{formatCurrency(r.total, r.currency)}</div></div>
              <div className="card-flat p-4"><div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Paid</div><div className="font-mono-data text-lg mt-1 text-[#2F6B4A]">{formatCurrency(r.paid, r.currency)}</div></div>
              <div className="card-flat p-4 border-l-4 border-l-[#E07A5F]">
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Outstanding</div>
                <div className="font-mono-data text-lg mt-1" data-testid={`ebv-outstanding-${r.currency}`}>{formatCurrency(r.outstanding, r.currency)}</div>
              </div>
            </div>
          ))}
          <div className="card-flat overflow-hidden">
            {visible.length === 0 ? (
              <div className="p-10 text-center text-sm text-[var(--muted-text)]">
                No bills in this period{data.rows.length ? " on the selected rail" : ""}.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
                  <tr>
                    <th className="text-left px-5 py-2.5">Vendor</th>
                    <th className="text-left px-3 py-2.5">Ccy</th>
                    <th className="text-right px-4 py-2.5">Bills</th>
                    <th className="text-right px-4 py-2.5">Total</th>
                    <th className="text-right px-4 py-2.5">Paid</th>
                    <th className="text-right px-4 py-2.5">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((r) => (
                    <tr key={`${r.vendor_id}-${r.currency}`} data-testid={`ebv-row-${r.vendor_id}-${r.currency}`}
                        className="border-b border-[var(--divider)] last:border-none cursor-pointer hover:bg-[var(--hover)]"
                        title="Click to drill into outstanding bills for this vendor"
                        onClick={() => setDrill({
                          title: `${r.vendor_name} · ${formatCurrency(r.outstanding, r.currency)} outstanding`,
                          params: { kind: "payable", vendor_id: r.vendor_id },
                          currency: r.currency,
                        })}>
                      <td className="px-5 py-2.5 font-medium">
                        {r.vendor_name}
                        <span className="ml-1.5 text-[10px] text-[var(--muted-text)]">↗</span>
                      </td>
                      <td className="px-3 py-2.5 font-mono text-[10px]"><span className="px-1.5 py-0.5 rounded bg-[var(--hover)]">{r.currency || "USD"}</span></td>
                      <td className="px-4 py-2.5 text-right font-mono">{r.bills}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(r.total, r.currency)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-[#2F6B4A]">{formatCurrency(r.paid, r.currency)}</td>
                      <td className={`px-4 py-2.5 text-right font-mono ${r.outstanding > 0 ? "text-[#B4283A]" : ""}`}>{formatCurrency(r.outstanding, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={drill?.currency || "USD"}
      />
    </div>
  );
}
